import { useContext, useEffect, useState, JSX } from 'react';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import { AlignVariant, DisplayVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import './styles.scss';
import DataListsProxy from 'api/dataLists/dataListsProxy';
import { ProgramSelectOption, SelectOption } from 'types/inputProps';
import LabeledSingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect/LabeledSingleSelect';
import { SingleStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/SingleSelect/styles';
import { LabeledDatePickerInput } from 'global_elements/Inputs/LabeledDatePickerInput';
import Button from 'global_elements/Button';
import { ButtonVariant } from 'global_elements/Button/variants';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import InlineText from 'global_elements/Text/InlineText';
import { Roles } from 'constants/roles';
import { ActiveFacilityContext } from 'context/activeFacility';
import { UserContext } from 'context/user';
import { SelectedReportFilters } from 'types/ReportGlobalFilters';
import LabeledMultiSelect from 'global_elements/Inputs/Dropdown/MultiSelect/LabeledMultiSelect';
import { MultiStandardDropdownStyles } from 'global_elements/Inputs/Dropdown/MultiSelect/styles';
import { ReportParameters } from 'constants/reports';
import SelectableRadioButton from 'global_elements/SelectableRadioButton/SelectableRadioButton';
import { subtractDay, getLastQuarterEnd, getLastQuarterStart, getThisQuarterStart } from 'domain/dateTime/DateHelpers';
import Paragraph from 'global_elements/Text/Paragraph';

export type ReportFiltersProps = {
  requiredParameters: string[];
  dateTypeOptions: SelectOption[];
  programFilter: string | null;
  programFilterID: string | null;
};

const allOption = {
  label: 'All',
  value: '*',
};

const currDate = new Date();
const currYear = currDate.getFullYear();
const currMonth = currDate.getMonth() + 1; // GetMonth() returns 0-indexed month

const lastMonth = currMonth === 1 ? 12 : currMonth - 1;
const lastMonthYear = currMonth === 1 ? currYear - 1 : currYear;

const thisMonthStart = new Date(`${currMonth}/1/${currYear}`);

const lastMonthStart = new Date(`${lastMonth}/1/${lastMonthYear}`);
const lastMonthEnd = subtractDay(thisMonthStart);

const last12MonthsStart = new Date(`${currMonth}/1/${currYear - 1}`);
const last12MonthsEnd = subtractDay(thisMonthStart);

const thisQuarterStart = getThisQuarterStart(currMonth, currYear);
const lastQuarterStart = getLastQuarterStart(currMonth, currYear);
const lastQuarterEnd = getLastQuarterEnd(currMonth, currYear);

const last4QuartersStart = new Date(`${thisQuarterStart.getMonth() + 1}/1/${currYear - 1}`);
const last4QuartersEnd = subtractDay(thisQuarterStart);

const thisYearStart = new Date(`1/1/${currYear}`);
const lastYearStart = new Date(`1/1/${currYear - 1}`);
const lastYearEnd = new Date(`12/31/${currYear - 1}`);

const maxEndDate = subtractDay(currDate);

function programArraysAreEqual(a: ProgramSelectOption[] | undefined, b: ProgramSelectOption[]): boolean {
  if (!a) return false;
  if (a === b) return true;
  if (a.length !== b.length) return false;

  // Create deep copies to keep from modifying original arrays
  const copyOfA = Array.from(a);
  const copyOfB = Array.from(b);

  copyOfA.sort();
  copyOfB.sort();

  for (let i = 0; i < copyOfA.length; i += 1) {
    if (copyOfA[i].value !== copyOfB[i].value) return false;
  }
  return true;
}

export default function ReportFiltersComponent(props: ReportFiltersProps): JSX.Element {
  const [programOptions, setProgramOptions] = useState<ProgramSelectOption[]>([]);
  const [treatmentTypeOptions, setTreatmentTypeOptions] = useState<SelectOption[]>([]);
  const [populationOptions, setPopulationOptions] = useState<SelectOption[]>([]);
  const { user, selectedReportFilters, SetSelectedReportFilters } = useContext(UserContext);
  const [reportFilters, setReportFilters] = useState<SelectedReportFilters>(
    selectedReportFilters ?? {
      startDate: null,
      endDate: null,
      dateType: { value: '', label: '' },
      dateRangeTypeID: -1,
      dateRangeTypeDesc: '',
      populationTypes: [],
      treatmentTypes: [],
      programs: [],
    },
  );
  const { facility } = useContext(ActiveFacilityContext);
  const contextFacilityFiltered = facility.value !== '' ? facility : undefined;
  const facilityID = contextFacilityFiltered !== undefined ? parseInt(contextFacilityFiltered.value, 10) : -1;
  const isFacilityUser = user?.role === Roles.FACILITY_DIRECTOR || user?.role === Roles.FACILITY_EXECUTIVE;
  const isCorpUser = user?.role === Roles.CORPORATION || user?.role === Roles.SUPER_CORPORATION;
  const showFacilityParams = isFacilityUser || (isCorpUser && facilityID !== 0);
  const showCorpParams = isCorpUser && !showFacilityParams;
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [hasAllProgramsSelected, setHasAllProgramsSelected] = useState<boolean>(Boolean(selectedReportFilters && selectedReportFilters.programs.includes(allOption)));
  const [isClearingDropdowns, setIsClearingDropdowns] = useState<boolean>(false);

  const loadProgramOptions = (fid: number): void => {
    setProgramOptions([]);
    DataListsProxy.getPrograms(fid, undefined, props.programFilter, props.programFilterID).then((results) => {
      const options: ProgramSelectOption[] = results.map((x) => ({
        label: x.reportProgramName ?? '',
        value: x.programID.toString(),
        populationTreatmentID: x.populationTreatmentID ?? -1,
      }));
      // console.log(options);
      setProgramOptions(options);
      if (hasAllProgramsSelected) {
        if (!programArraysAreEqual(reportFilters.programs, [allOption, ...options])) {
          setReportFilters((prevState) => ({
            ...prevState,
            programs: [allOption, ...options],
          }));
        }
        if (!programArraysAreEqual(selectedReportFilters?.programs, [allOption, ...options])) {
          if (selectedReportFilters) {
            SetSelectedReportFilters({
              ...selectedReportFilters,
              programs: [allOption, ...options],
            });
          }
        }
      }
    });
  };

  const loadPopulationTypeOptions = (fid: number): void => {
    setPopulationOptions([]);
    DataListsProxy.getListPopulation(fid).then((results) => {
      const options = results.map((x) => ({
        label: x.populationType,
        value: x.populationTypeID.toString(),
      }));
      setPopulationOptions(options);
    });
  };

  const loadTreatmentTypeOptions = (fid: number): void => {
    setTreatmentTypeOptions([]);
    DataListsProxy.getLevelOfCareList(fid).then((results) => {
      const options = results.map((x) => ({
        label: x.treatmentType,
        value: x.treatmentTypeID.toString(),
      }));
      setTreatmentTypeOptions(options);
    });
  };

  const hasSelectableParameters = (): boolean => {
    for (let i = 0; i < props.requiredParameters.length; i += 1) {
      if (
        props.requiredParameters[i] !== ReportParameters.ParentFacilityID
        && props.requiredParameters[i] !== ReportParameters.FacilityName
        && props.requiredParameters[i] !== ReportParameters.FacilityID
        && props.requiredParameters[i] !== ReportParameters.ParentFacilityName
        && props.requiredParameters[i] !== ReportParameters.AccountID
      ) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (facilityID !== -1) {
      if (!isLoadingPage) {
        setReportFilters((prevState) => ({
          ...prevState,
          programs: [],
        }));
        if (selectedReportFilters) {
          SetSelectedReportFilters({
            ...selectedReportFilters,
            programs: [],
          });
        }
      }

      if (showFacilityParams) {
        loadProgramOptions(facilityID);
      }
      if (showCorpParams) {
        loadPopulationTypeOptions(facilityID);
        loadTreatmentTypeOptions(facilityID);
      }
      setIsLoadingPage(false);
    }
  }, [facilityID]);

  const handleDropdownSelect = (option: SelectOption, fieldName: string): void => {
    setReportFilters((prevState) => ({
      ...prevState,
      [fieldName]: option,
    }));
  };

  const handleDropdownMultiSelect = (options: SelectOption[] | ProgramSelectOption[], fieldName: string): void => {
    setReportFilters((prevState) => ({
      ...prevState,
      [fieldName]: options,
    }));
  };

  const handleDatepickerSelect = (date: Date | null, fieldName: string): void => {
    setReportFilters((prevState) => ({
      ...prevState,
      [fieldName]: date,
    }));
  };

  const applyFilters = (): void => {
    setHasAllProgramsSelected(reportFilters.programs.includes(allOption));
    SetSelectedReportFilters(reportFilters);
  };

  const onDateRangeButtonClick = (value: number, label: string, startDate?: Date, endDate?: Date): void => {
    if (startDate && endDate) {
      setReportFilters((prevState) => ({
        ...prevState,
        dateRangeTypeID: value,
        dateRangeTypeDesc: label,
        startDate,
        endDate,
      }));
    } else {
      setReportFilters((prevState) => ({
        ...prevState,
        dateRangeTypeID: value,
        dateRangeTypeDesc: label,
        startDate: null,
        endDate: null,
      }));
    }
  };

  function areArraysEqual(a: unknown[], b: unknown[]): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  const filtersTouched = (): boolean => reportFilters.dateType.value !== selectedReportFilters?.dateType.value
    || reportFilters.startDate?.getTime() !== selectedReportFilters.startDate?.getTime()
    || reportFilters.endDate?.getTime() !== selectedReportFilters.endDate?.getTime()
    || !areArraysEqual(reportFilters.programs, selectedReportFilters.programs)
    || !areArraysEqual(reportFilters.populationTypes, selectedReportFilters.populationTypes)
    || !areArraysEqual(reportFilters.treatmentTypes, selectedReportFilters.treatmentTypes);

  const invalidDate = (dateType: ReportParameters): string => {
    if (!reportFilters) {
      return '';
    }
    if (dateType === ReportParameters.StartDate) {
      if (!reportFilters.startDate) {
        return '';
      }
      if (reportFilters?.endDate && reportFilters.startDate > reportFilters.endDate) {
        return 'Start date cannot be later than end date.';
      }
      if (reportFilters.startDate > currDate) {
        return 'Start date cannot be in the future.';
      }
      return '';
    }

    // End date calculations
    if (!reportFilters.endDate) {
      return '';
    }
    if (reportFilters.endDate > currDate) {
      return 'End date cannot be in the future.';
    }
    return '';
  };

  const startDateRequired = (): boolean => {
    let foundStartParameter = false;
    for (let i = 0; i < props.requiredParameters.length; i += 1) {
      if (props.requiredParameters[i] === ReportParameters.StartDate || props.requiredParameters[i] === ReportParameters.StartMonth || props.requiredParameters[i] === ReportParameters.StartYear) {
        foundStartParameter = true;
        break;
      }
    }
    return foundStartParameter;
  };
  const endDateRequired = (): boolean => {
    let foundEndParameter = false;
    for (let i = 0; i < props.requiredParameters.length; i += 1) {
      if (props.requiredParameters[i] === ReportParameters.EndDate || props.requiredParameters[i] === ReportParameters.EndMonth || props.requiredParameters[i] === ReportParameters.EndYear) {
        foundEndParameter = true;
        break;
      }
    }

    return foundEndParameter;
  };

  const parametersAreValid = (parametersList: SelectedReportFilters | null): boolean => {
    for (let i = 0; i < props.requiredParameters.length; i += 1) {
      if (props.requiredParameters[i] === ReportParameters.DateType && parametersList?.dateType.value === '') {
        return false;
      }
      if (
        (props.requiredParameters[i] === ReportParameters.StartDate || props.requiredParameters[i] === ReportParameters.StartMonth || props.requiredParameters[i] === ReportParameters.StartYear)
        && (!parametersList?.startDate || invalidDate(ReportParameters.StartDate))
      ) {
        return false;
      }
      if (
        (props.requiredParameters[i] === ReportParameters.EndDate || props.requiredParameters[i] === ReportParameters.EndMonth || props.requiredParameters[i] === ReportParameters.EndYear)
        && (!parametersList?.endDate || invalidDate(ReportParameters.EndDate))
      ) {
        return false;
      }
      if (props.requiredParameters[i] === ReportParameters.ProgramID && parametersList?.programs.length === 0) {
        return false;
      }
      if (props.requiredParameters[i] === ReportParameters.TreatmentTypeID && parametersList?.treatmentTypes.length === 0) {
        return false;
      }
      if (props.requiredParameters[i] === ReportParameters.PopulationTypeID && parametersList?.populationTypes.length === 0) {
        return false;
      }
    }

    return true;
  };

  const hasFiltersSelected = (): boolean => reportFilters.startDate !== null
    || reportFilters.endDate !== null
    || reportFilters.dateType.value !== ''
    || reportFilters.dateRangeTypeID !== -1
    || reportFilters.populationTypes.length > 0
    || reportFilters.treatmentTypes.length > 0
    || reportFilters.programs.length > 0;

  const resetFilters = (): void => {
    setIsClearingDropdowns(true);
    setHasAllProgramsSelected(false);
    setReportFilters({
      startDate: null,
      endDate: null,
      dateType: { value: '', label: '' },
      dateRangeTypeID: -1,
      dateRangeTypeDesc: '',
      populationTypes: [],
      treatmentTypes: [],
      programs: [],
    });

    SetSelectedReportFilters({
      startDate: null,
      endDate: null,
      dateType: { value: '', label: '' },
      dateRangeTypeID: -1,
      dateRangeTypeDesc: '',
      populationTypes: [],
      treatmentTypes: [],
      programs: [],
    });

    setTimeout(() => {
      setIsClearingDropdowns(false);
    }, 50);
  };

  if (!hasSelectableParameters()) {
    return (
      <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="filter-container">
        <InlineText text="No parameters to select." fontColor={FontColors.PRIMARY} fontSize={FontSizes.LARGE} extraClasses="no-parameters-message" />
      </FlexContainer>
    );
  }

  return (
    <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="filter-container">
      {props.requiredParameters.includes(ReportParameters.DateType) && (props.dateTypeOptions.length === 0 || isClearingDropdowns) && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledSingleSelect
            styles={SingleStandardDropdownStyles}
            label="Date Type"
            options={props.dateTypeOptions}
            defaultValue={reportFilters.dateType.value !== '' ? reportFilters.dateType : { label: 'Loading...', value: '' }}
            value={reportFilters.dateType.value !== '' ? reportFilters.dateType : { label: 'Loading...', value: '' }}
            onSelection={(option) => handleDropdownSelect(option, 'dateType')}
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      {props.requiredParameters.includes(ReportParameters.DateType) && props.dateTypeOptions.length > 0 && !isClearingDropdowns && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledSingleSelect
            styles={SingleStandardDropdownStyles}
            label="Date Type"
            options={props.dateTypeOptions}
            defaultValue={reportFilters.dateType.value !== '' ? reportFilters.dateType : { label: 'Select a Date Type', value: '' }}
            value={reportFilters.dateType.value !== '' ? reportFilters.dateType : undefined}
            onSelection={(option) => handleDropdownSelect(option, 'dateType')}
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      {(startDateRequired() || endDateRequired()) && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START}>
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START}>
            <Paragraph text="Date Range" fontColor={FontColors.PRIMARY} fontSize={FontSizes.EXTRA_LARGE} bold extraClasses="labeled-multi-select__header" />
            <Paragraph text="*" fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.EXTRA_LARGE} bold />
          </FlexContainer>
          <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.START} wrap extraClasses="date-range-button-container">
            <SelectableRadioButton
              title="This Month"
              value={1}
              onClick={(value: number) => onDateRangeButtonClick(value, 'This Month', thisMonthStart, maxEndDate < thisMonthStart ? thisMonthStart : maxEndDate)}
              isSelected={reportFilters.dateRangeTypeID === 1}
            />
            <SelectableRadioButton
              title="Last Month"
              value={2}
              onClick={(value: number) => onDateRangeButtonClick(value, 'Last Month', lastMonthStart, lastMonthEnd)}
              isSelected={reportFilters.dateRangeTypeID === 2}
            />
            <SelectableRadioButton
              title="Last 12 Months"
              value={3}
              onClick={(value: number) => onDateRangeButtonClick(value, 'Last 12 Months', last12MonthsStart, last12MonthsEnd)}
              isSelected={reportFilters.dateRangeTypeID === 3}
            />
            <SelectableRadioButton
              title="This Quarter"
              value={4}
              onClick={(value: number) => onDateRangeButtonClick(value, 'This Quarter', thisQuarterStart, maxEndDate < thisQuarterStart ? thisQuarterStart : maxEndDate)}
              isSelected={reportFilters.dateRangeTypeID === 4}
            />
            <SelectableRadioButton
              title="Last Quarter"
              value={5}
              onClick={(value: number) => onDateRangeButtonClick(value, 'Last Quarter', lastQuarterStart, lastQuarterEnd)}
              isSelected={reportFilters.dateRangeTypeID === 5}
            />
            <SelectableRadioButton
              title="Last 4 Quarters"
              value={6}
              onClick={(value: number) => onDateRangeButtonClick(value, 'Last 4 Quarters', last4QuartersStart, last4QuartersEnd)}
              isSelected={reportFilters.dateRangeTypeID === 6}
            />
            <SelectableRadioButton
              title="This Year"
              value={7}
              onClick={(value: number) => onDateRangeButtonClick(value, 'This Year', thisYearStart, maxEndDate < thisYearStart ? thisYearStart : maxEndDate)}
              isSelected={reportFilters.dateRangeTypeID === 7}
            />
            <SelectableRadioButton
              title="Last Year"
              value={8}
              onClick={(value: number) => onDateRangeButtonClick(value, 'Last Year', lastYearStart, lastYearEnd)}
              isSelected={reportFilters.dateRangeTypeID === 8}
            />
            <SelectableRadioButton title="Custom" value={9} onClick={(value: number) => onDateRangeButtonClick(value, 'Custom')} isSelected={reportFilters.dateRangeTypeID === 9} />
          </FlexContainer>
          {reportFilters.dateRangeTypeID !== -1 && reportFilters.dateRangeTypeID !== 9 && (
            <InlineText
              text={`Selected Dates: ${reportFilters.startDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })} - ${reportFilters.endDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}`}
              fontColor={FontColors.PRIMARY}
              fontSize={FontSizes.REGULAR}
              bold
              extraClasses="selected-dates-label"
            />
          )}
        </FlexContainer>
      )}
      {startDateRequired() && reportFilters.dateRangeTypeID === 9 && (
        <LabeledDatePickerInput
          label="Start Date"
          name="startDate"
          onChange={(selectedDate) => handleDatepickerSelect(selectedDate, 'startDate')}
          error={invalidDate(ReportParameters.StartDate)}
          testid="from-date-input"
          initialValue={reportFilters.startDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
          placeholder="MMDDYYYY"
        />
      )}
      {endDateRequired() && reportFilters.dateRangeTypeID === 9 && (
        <LabeledDatePickerInput
          label="End Date"
          name="endDate"
          onChange={(selectedDate) => handleDatepickerSelect(selectedDate, 'endDate')}
          error={invalidDate(ReportParameters.EndDate)}
          testid="to-date-input"
          initialValue={reportFilters.endDate?.toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}
          placeholder="MMDDYYYY"
        />
      )}
      {props.requiredParameters.includes(ReportParameters.ProgramID) && showFacilityParams && (programOptions.length === 0 || isClearingDropdowns) && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledMultiSelect
            label="Programs"
            placeholder="Select programs"
            styles={MultiStandardDropdownStyles}
            options={programOptions}
            defaultValue={reportFilters.programs.length > 0 ? reportFilters.programs : [{ label: 'Loading...', value: '' }]}
            value={reportFilters.programs.length > 0 ? reportFilters.programs : [{ label: 'Loading...', value: '' }]}
            onSelection={(options) => handleDropdownMultiSelect(options, 'programs')}
            closeMenuOnSelect={false}
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      {props.requiredParameters.includes(ReportParameters.ProgramID) && showFacilityParams && programOptions.length > 0 && !isClearingDropdowns && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledMultiSelect
            label="Programs"
            placeholder="Select programs"
            styles={MultiStandardDropdownStyles}
            options={programOptions}
            defaultValue={reportFilters.programs}
            value={reportFilters.programs}
            onSelection={(options) => handleDropdownMultiSelect(options, 'programs')}
            closeMenuOnSelect={false}
            allowSelectAll
            allOption={allOption}
            hideNoOptionsMessage
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      {props.requiredParameters.includes(ReportParameters.PopulationTypeID) && showCorpParams && (populationOptions.length === 0 || isClearingDropdowns) && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledMultiSelect
            label="Populations"
            placeholder="Select populations"
            styles={MultiStandardDropdownStyles}
            options={[{ label: 'Loading...', value: '' }]}
            defaultValue={reportFilters.populationTypes.length > 0 ? reportFilters.populationTypes : [{ label: 'Loading...', value: '' }]}
            value={reportFilters.populationTypes.length > 0 ? reportFilters.populationTypes : [{ label: 'Loading...', value: '' }]}
            onSelection={(options) => handleDropdownMultiSelect(options, 'populationTypes')}
            closeMenuOnSelect={false}
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      {props.requiredParameters.includes(ReportParameters.PopulationTypeID) && showCorpParams && populationOptions.length > 0 && !isClearingDropdowns && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledMultiSelect
            label="Populations"
            placeholder="Select populations"
            styles={MultiStandardDropdownStyles}
            options={populationOptions}
            defaultValue={reportFilters.populationTypes}
            value={reportFilters.populationTypes}
            onSelection={(options) => handleDropdownMultiSelect(options, 'populationTypes')}
            closeMenuOnSelect={false}
            allowSelectAll
            allOption={allOption}
            hideNoOptionsMessage
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      {props.requiredParameters.includes(ReportParameters.TreatmentTypeID) && showCorpParams && (treatmentTypeOptions.length === 0 || isClearingDropdowns) && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledMultiSelect
            label="Level of Care"
            placeholder="Select levels of care"
            styles={MultiStandardDropdownStyles}
            options={[{ label: 'Loading...', value: '' }]}
            defaultValue={reportFilters.treatmentTypes.length > 0 ? reportFilters.treatmentTypes : [{ label: 'Loading...', value: '' }]}
            value={reportFilters.treatmentTypes.length > 0 ? reportFilters.treatmentTypes : [{ label: 'Loading...', value: '' }]}
            onSelection={(options) => handleDropdownMultiSelect(options, 'treatmentTypes')}
            closeMenuOnSelect={false}
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      {props.requiredParameters.includes(ReportParameters.TreatmentTypeID) && showCorpParams && treatmentTypeOptions.length > 0 && !isClearingDropdowns && (
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="dropdown-input-container">
          <LabeledMultiSelect
            label="Levels of Care"
            placeholder="Select levels of care"
            styles={MultiStandardDropdownStyles}
            options={treatmentTypeOptions}
            defaultValue={reportFilters.treatmentTypes}
            value={reportFilters.treatmentTypes}
            onSelection={(options) => handleDropdownMultiSelect(options, 'treatmentTypes')}
            closeMenuOnSelect={false}
            allowSelectAll
            allOption={allOption}
            hideNoOptionsMessage
            labelFontSize={FontSizes.EXTRA_LARGE}
            labelFontColor={FontColors.PRIMARY}
            showRequiredAsterisk
          />
        </FlexContainer>
      )}
      <FlexContainer display={DisplayVariant.FLEX_ROW} align={AlignVariant.START} justify={JustifyVariant.SPACE_BETWEEN} wrap extraClasses="filter-button-container">
        <Button variant={ButtonVariant.PRIMARY} onClick={resetFilters} extraClasses="apply-button" disabled={!hasFiltersSelected()}>
          <InlineText text="Reset" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
        </Button>
        {props.requiredParameters.length > 0 && (
          <Button variant={ButtonVariant.PRIMARY} onClick={applyFilters} extraClasses="apply-button" disabled={!parametersAreValid(reportFilters) || !filtersTouched()}>
            <InlineText text="Apply" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
        )}
      </FlexContainer>
    </FlexContainer>
  );
}
